/**
 * Email Verification Page
 *
 * Page for users who need to verify their email address
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/auth';
import { Alert } from '../../components/ui/Alert';
import { trackEmailConfirmed } from '../../lib/gtag';

export default function VerifyEmailPage() {
  const { t } = useTranslation('auth');
  const { user, isAuthenticated, fetchUser } = useAuthStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const emailConfirmedFired = useRef(false);

  useEffect(() => {
    if (user?.email_verified_at && !emailConfirmedFired.current) {
      emailConfirmedFired.current = true;
      trackEmailConfirmed();
    }
  }, [user?.email_verified_at]);

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Refresh user data periodically to check if email was verified
  useEffect(() => {
    if (!user?.email_verified_at) {
      const interval = setInterval(() => {
        fetchUser();
      }, 10000); // Check every 10 seconds
      return () => clearInterval(interval);
    }
  }, [user?.email_verified_at, fetchUser]);

  // Handle verification status from URL parameters
  useEffect(() => {
    const status = searchParams.get('status');
    const error = searchParams.get('error');

    if (status === 'already-verified') {
      toast.success(t('verify_email.toast_already_verified'));
      // Refresh user data to show verified state
      fetchUser();
      // Clean up URL
      navigate('/verify-email', { replace: true });
    } else if (status === 'expired') {
      setVerificationError(t('verify_email.error_expired'));
      toast.error(t('verify_email.toast_expired'));
      navigate('/verify-email', { replace: true });
    } else if (status === 'invalid') {
      setVerificationError(t('verify_email.error_invalid'));
      toast.error(t('verify_email.toast_invalid'));
      navigate('/verify-email', { replace: true });
    } else if (error) {
      setVerificationError(t('verify_email.error_generic'));
      toast.error(t('verify_email.toast_failed'));
      navigate('/verify-email', { replace: true });
    }
  }, [searchParams, navigate, fetchUser, t]);

  const handleResend = async () => {
    try {
      setIsResending(true);
      setVerificationError(null); // Clear any previous errors
      await authService.resendVerificationEmail();
      toast.success(t('verify_email.toast_sent'));
      setResendCooldown(60); // 60 second cooldown
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || t('verify_email.error_send_failed');

      // Handle rate limiting
      if (err?.response?.status === 429) {
        setVerificationError(t('verify_email.error_rate_limit'));
        toast.error(t('verify_email.toast_rate_limit'));
      } else {
        setVerificationError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setIsResending(false);
    }
  };

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('verify_email.not_authenticated_title')}</h2>
            <p className="text-gray-600 mb-6">
              {t('verify_email.not_authenticated_message')}
            </p>
            <Link to="/login" className="btn btn-primary">
              {t('verify_email.go_to_login')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Email already verified - show success
  if (user?.email_verified_at) {
    return (
      <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('verify_email.success_title')}</h2>
            <p className="text-gray-600 mb-2">
              {t('verify_email.success_message')}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {t('verify_email.verified_on')} {new Date(user.email_verified_at).toLocaleDateString()}
            </p>
            <Link to="/dashboard" className="btn btn-primary">
              {t('verify_email.go_to_dashboard')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Awaiting verification
  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            {/* Icon */}
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 mb-4">
              <svg
                className="h-6 w-6 text-indigo-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('verify_email.title')}</h2>

            {/* Error Alert */}
            {verificationError && (
              <Alert variant="error" className="mb-6 text-left" onClose={() => setVerificationError(null)}>
                {verificationError}
              </Alert>
            )}

            {/* Message */}
            <div className="mb-6 text-left">
              <p className="text-gray-600 mb-4">
                {t('verify_email.sent_to')}
              </p>
              <p className="text-center font-medium text-gray-900 mb-4">{user?.email}</p>
              <p className="text-sm text-gray-500">
                {t('verify_email.check_inbox')}
              </p>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-sm font-medium text-blue-900 mb-2">{t('verify_email.what_to_do')}</h3>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>{t('verify_email.step_1')}</li>
                <li>{t('verify_email.step_2')}</li>
                <li>{t('verify_email.step_3')}</li>
              </ol>
            </div>

            {/* Resend button */}
            <button
              onClick={handleResend}
              disabled={isResending || resendCooldown > 0}
              className="btn btn-secondary w-full mb-4"
            >
              {isResending
                ? t('verify_email.resending')
                : resendCooldown > 0
                ? t('verify_email.resend_cooldown', { seconds: resendCooldown })
                : t('verify_email.resend_button')}
            </button>

            {/* Help text */}
            <div className="text-sm text-gray-500 space-y-2">
              <p>{t('verify_email.didnt_receive')}</p>
              <ul className="list-disc list-inside text-left">
                <li>{t('verify_email.tip_1')}</li>
                <li>{t('verify_email.tip_2', { email: user?.email })}</li>
                <li>{t('verify_email.tip_3')}</li>
              </ul>
            </div>

            {/* Back to dashboard link */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <Link to="/dashboard" className="text-sm text-indigo-600 hover:text-indigo-500">
                {t('verify_email.continue_to_dashboard')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
